import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useProdAuth } from '../../contexts/ProdAuthContext';

interface Farmer {
  id: string;
  memberId: string;
  name: string;
  plotSizeHa: string;
  county: string;
  ward: string;
  collectionCentreId: string;
  active: boolean;
}

const PAGE_SIZE = 50;
const ACCENT = '#4A7C59';

export const FarmerList: React.FC = () => {
  const { user } = useProdAuth();
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);

  // Inline name editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const fetchFarmers = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(PAGE_SIZE),
    });
    if (search) params.set('search', search);

    try {
      const res = await fetch(`/api/production/farmers?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setFarmers(data.farmers || []);
      setTotal(data.total || 0);
    } catch {
      setFarmers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchFarmers();
  }, [fetchFarmers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const canWrite = ['station', 'admin', 'super_admin', 'operations'].includes(user?.role || '');

  const startEditing = (f: Farmer) => {
    if (!canWrite) return;
    setEditingId(f.id);
    setEditingName(f.name || '');
  };

  const saveName = async (id: string) => {
    const farmer = farmers.find(f => f.id === id);
    if (!farmer || editingName === (farmer.name || '')) {
      setEditingId(null);
      return;
    }
    setSavingId(id);
    try {
      const res = await fetch(`/api/production/farmers?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editingName }),
      });
      if (res.ok) {
        setFarmers(fs => fs.map(f => f.id === id ? { ...f, name: editingName } : f));
      }
    } finally {
      setSavingId(null);
      setEditingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Farmers</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">
            {total.toLocaleString()} registered
            {canWrite && farmers.filter(f => !f.name).length > 0 && (
              <span className="ml-2 text-amber-600">· {farmers.filter(f => !f.name).length} on this page missing names</span>
            )}
          </p>
        </div>
        {canWrite && (
          <Link
            to="/production/farmers/new"
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#4A7C59' }}
          >
            Add farmer
          </Link>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6 flex gap-2">
        <input
          type="text"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search by member ID, name, or ward..."
          className="flex-1 max-w-sm px-4 py-2 border border-[#E8E6E2] rounded-lg bg-white text-sm text-[#1A1A1A] placeholder-[#A3A3A3] focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/30"
        />
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: '#4A7C59' }}
        >
          Search
        </button>
        {search && (
          <button
            type="button"
            onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
            className="px-4 py-2 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]"
          >
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      <div className="bg-white border border-[#E8E6E2] rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
        ) : farmers.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#6B6B6B]">
            {search ? `No farmers matching "${search}"` : 'No farmers registered yet'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E2] bg-[#FAFAF8]">
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Member ID</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Name</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Plot</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">County</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Ward</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {farmers.map((f, i) => (
                <tr
                  key={f.id}
                  className={`border-b border-[#E8E6E2] last:border-0 ${i % 2 === 0 ? '' : 'bg-[#FAFAF8]'}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-[#1A1A1A]">{f.memberId}</td>
                  <td className="px-4 py-3 text-[#1A1A1A]">
                    {editingId === f.id ? (
                      <input
                        autoFocus
                        type="text"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={() => saveName(f.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveName(f.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="w-full px-2 py-1 border border-[#4A7C59] rounded text-sm text-[#1A1A1A] bg-white focus:outline-none"
                      />
                    ) : (
                      <span
                        onClick={() => startEditing(f)}
                        className={`block ${canWrite ? 'cursor-pointer hover:text-[#4A7C59]' : ''} ${!f.name ? 'text-[#A3A3A3]' : ''}`}
                        title={canWrite ? 'Click to edit name' : undefined}
                      >
                        {savingId === f.id ? '...' : (f.name || (canWrite ? <span className="italic text-[#C0BDB9]">click to add name</span> : '—'))}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#6B6B6B]">{f.plotSizeHa || '—'}</td>
                  <td className="px-4 py-3 text-[#6B6B6B]">{f.county || '—'}</td>
                  <td className="px-4 py-3 text-[#6B6B6B]">{f.ward || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${
                      f.active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
                    }`}>
                      {f.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-[#6B6B6B]">
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 border border-[#E8E6E2] rounded-lg disabled:opacity-40 hover:bg-[#F0EDE8] transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 border border-[#E8E6E2] rounded-lg disabled:opacity-40 hover:bg-[#F0EDE8] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
